import React from 'react';
import {
  applyTypography,
  BaseButton,
  trackWorkshopBookStart,
  useT,
} from '@repo/common';
import { ReactComponent as ShoppingBagIcon32 } from '@repo/common/assets/icons/32px/icon_32px_shopping-bag.svg';
import { ReactComponent as HeartOutlineIcon32 } from '@repo/common/assets/icons/32px/icon_32px_heart_outline.svg';
import { ReactComponent as HeartFilledIcon32 } from '@repo/common/assets/icons/32px/icon_32px_heart_filled.svg';
import { formatWorkshopPrice } from '../utils/priceUtils';

const StickyBookingBar = ({
  data,
  title = 'Workshop name',
  dateSlot = null,
  availability = '1 left',
  price = '15.00$',
  // 숫자로 price 가 올 때 쓰는 통화 (KRW → "55,000won", USD → "15.00$")
  currency = 'USD',
  isWishlisted = false,
  isInBag = false,
  bookNowText = 'Book now',
  bookNowSubText = '',
  bookNowDisabled = false,
  bookNowVariant = 'primary',
  hidePrice = false,
  cartDisabled = false,
  onWishlistToggle,
  onBookNow,
  onAddToCart,
}) => {
  const t = useT();
  const getDisplayPrice = (rawPrice) => {
    if (rawPrice === null || rawPrice === undefined || rawPrice === '') {
      return rawPrice;
    }

    if (typeof rawPrice === 'number') {
      return rawPrice <= 0
        ? t('Free', '무료')
        : formatWorkshopPrice(rawPrice, currency);
    }

    const text = String(rawPrice).trim();
    if (!text) return text;
    if (text.toLowerCase() === 'free') return t('Free', '무료');

    const numericValue = text.replace(/USD|usd|\$/g, '').trim();
    const num = parseFloat(numericValue);
    if (!Number.isNaN(num) && num <= 0) return t('Free', '무료');

    return text;
  };

  const displayPrice = getDisplayPrice(price);
  const hasBookNowSubText = Boolean(bookNowSubText);
  const isTextBookNow = bookNowVariant === 'text';

  const handleBookNow = () => {
    if (bookNowDisabled) return;
    onBookNow && onBookNow();
    trackBookingStart();
  };

  const trackBookingStart = () => {
    const stepNumber =
      parseInt(String(data.step).replace(/\D/g, ''), 10) || null;

    trackWorkshopBookStart({
      workshop_id: data.id,
      workshop_name: data.title,
      step_number: stepNumber,
      step_name: data.step,
      source: 'detail_page_cta',
    });
  };

  const renderBookNowButton = (size) => (
    <BaseButton
      type={isTextBookNow ? 'text' : 'primary'}
      size={hasBookNowSubText ? 'xl' : size}
      hideIcon
      width='100%'
      className={hasBookNowSubText ? 'py-0' : undefined}
      isDisabled={bookNowDisabled}
      onClick={handleBookNow}
      bgclass={isTextBookNow ? 'bg-transparent' : undefined}
      borderclass={isTextBookNow ? 'border-transparent' : undefined}
      textclass={isTextBookNow ? 'text-text-primary' : undefined}
      hoverclass={
        isTextBookNow
          ? 'hover:bg-transparent hover:border-transparent hover:text-text-primary'
          : undefined
      }
      activeclass={
        isTextBookNow
          ? 'active:bg-transparent active:border-transparent active:text-text-primary'
          : undefined
      }
      textWrapperClass={
        hasBookNowSubText
          ? 'flex flex-col items-center justify-center gap-[2px]'
          : undefined
      }
    >
      {bookNowSubText && (
        <span
          className={`${applyTypography(
            'b12',
          )} text-center leading-[1.4] text-text-placeholder`}
        >
          {bookNowSubText}
        </span>
      )}
      <span
        className={`${applyTypography('b16Strong')} text-center leading-[1.5] ${
          isTextBookNow
            ? 'text-text-primary underline underline-offset-[3px]'
            : 'text-text-inverse'
        }`}
      >
        {bookNowText === 'Book now'
          ? t('Book now', '지금 예약하기')
          : bookNowText}
      </span>
    </BaseButton>
  );

  return (
    <div
      className='fixed bottom-0 left-0 right-0 z-[999] px-[16px] py-[16px] bg-[rgba(249,247,244,0.6)] backdrop-blur-[7.5px] tabletH:px-[40px] tabletH:py-[28px] tabletH:bg-transparent tabletH:backdrop-blur-0 tabletV:px-[80px]'
      style={{
        animation: 'slideUpFromBottom 0.2s ease-out forwards',
      }}
    >
      <div className='bg-grayscale-gray00 flex flex-col items-start justify-between overflow-clip p-[16px] relative rounded-[30px] shadow-popup-01 w-full tabletH:flex-row tabletH:items-center tabletH:gap-[16px] tabletH:px-[40px] tabletH:py-[26px] tabletH:rounded-[50px] tabletV:px-[80px]'>
        <div className='flex gap-[8px] items-center w-full tabletH:hidden'>
          {!hidePrice && (
            <div className='bg-grayscale-gray01 flex h-full items-center px-[16px] py-[8px] rounded-[8px] shrink-0'>
              <p
                className={`${applyTypography(
                  'b18Strong',
                )} text-text-tertiary shrink-0`}
                style={{ lineHeight: '1.5' }}
              >
                {displayPrice}
              </p>
            </div>
          )}
          <div className='min-w-0 flex-1'>{renderBookNowButton('l')}</div>
        </div>

        <div className='hidden tabletH:flex tabletH:flex-col tabletH:flex-[1_1_auto] tabletH:min-w-0 tabletH:gap-[2px] tabletH:items-start tabletH:justify-center tabletH:relative'>
          {dateSlot && (dateSlot.date || dateSlot.time) && (
            <p
              className={`${applyTypography(
                'b12Strong',
              )} text-text-secondary w-full`}
              style={{ lineHeight: '1.4' }}
            >
              {dateSlot.date && dateSlot.time
                ? `${dateSlot.date} - ${dateSlot.time}`
                : dateSlot.date || dateSlot.time}
            </p>
          )}
          <p
            className={`${applyTypography(
              'h5',
            )} text-text-primary w-full overflow-hidden text-ellipsis whitespace-nowrap`}
            style={{ lineHeight: '1.2' }}
          >
            {title}
          </p>
        </div>

        <div className='hidden tabletH:flex tabletH:gap-[16px] tabletH:items-center tabletH:relative tabletH:flex-[0_0_auto] tabletH:min-w-0'>
          <div className='flex flex-row items-center self-stretch tabletH:w-auto'>
            <div className='flex flex-nowrap gap-[8px] h-full items-center relative tabletH:w-auto'>
              <div className='bg-functional-accent01 flex h-full items-center px-[16px] py-[8px] rounded-[8px] shrink-0'>
                <p
                  className={`${applyTypography(
                    'b18Strong',
                  )} text-text-accent shrink-0`}
                  style={{ lineHeight: '1.5' }}
                >
                  {availability}
                </p>
              </div>
              {!hidePrice && (
                <div className='bg-grayscale-gray01 flex h-full items-center px-[16px] py-[8px] rounded-[8px] shrink-0'>
                  <p
                    className={`${applyTypography(
                      'b18Strong',
                    )} text-text-tertiary shrink-0`}
                    style={{ lineHeight: '1.5' }}
                  >
                    {displayPrice}
                  </p>
                </div>
              )}
              <div className='w-[180px] shrink-0'>
                {renderBookNowButton('xl')}
              </div>
            </div>
          </div>
          <BaseButton
            type='text'
            size='s'
            className={`relative size-[40px] min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent overflow-clip shrink-0 [&>span]:h-full ${
              cartDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            isDisabled={cartDisabled}
            onClick={cartDisabled ? undefined : onAddToCart}
            aria-label={
              cartDisabled
                ? t('Shopping bag unavailable', '장바구니 사용 불가')
                : t('Add to shopping bag', '장바구니에 담기')
            }
            icon={
              <ShoppingBagIcon32
                className={`w-full h-full ${
                  cartDisabled
                    ? 'text-grayscale-gray03'
                    : isInBag
                      ? 'text-text-primary'
                      : ''
                }`}
              />
            }
          />
          <BaseButton
            type='text'
            size='s'
            className='relative h-auto min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent overflow-clip shrink-0'
            onClick={onWishlistToggle}
            aria-label={
              isWishlisted
                ? t('Remove from wishlist', '위시리스트에서 제거')
                : t('Add to wishlist', '위시리스트에 추가')
            }
            icon={
              isWishlisted ? (
                <HeartFilledIcon32 className='w-full h-full text-icon-failed' />
              ) : (
                <HeartOutlineIcon32 className='w-full h-full' />
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default StickyBookingBar;
